import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { ExerciseMedia } from '@/components/ExerciseMedia';
import { MediaUploader } from '@/components/MediaUploader';
import { Button } from '@/components/ui/button';
import { Trash2, Copy, Check, Film } from 'lucide-react';
import { toast } from 'sonner';

function fmtSize(b) {
  if (!b) return '';
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// Video Library: lists uploaded exercise videos. If onPick is provided, clicking a card
// selects it (reuse across exercises); always supports copy-url and soft-delete.
export function VideoLibrary({ onPick }) {
  const [items, setItems] = useState(null);

  const load = async () => {
    try { const r = await api.get('/exercise-media'); setItems(r.data || []); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u?.startsWith('http') ? u : `${backend}${u}`);

  const copy = (u) => { navigator.clipboard?.writeText(fullUrl(u)); toast.success('URL copied'); };
  const del = async (id) => {
    if (!confirm('Remove this video from the library? (Exercises already using it keep working.)')) return;
    await api.delete(`/exercise-media/${id}`); toast.success('Removed'); load();
  };

  return (
    <div className="space-y-4">
      {!onPick && (
        <div className="regen-card-elevated p-4" data-testid="video-library-uploader">
          <div className="text-sm font-display font-bold mb-2">Upload a video</div>
          <MediaUploader onUploaded={() => load()} label="Upload exercise video" />
          <p className="text-[11px] text-muted-foreground mt-2">Uploaded videos are saved to your library and can be reused on any exercise.</p>
        </div>
      )}

      {items === null ? (
        <div className="text-muted-foreground text-sm py-10 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="regen-card-elevated p-10 text-center text-muted-foreground" data-testid="video-library-empty">
          <Film className="w-6 h-6 mx-auto mb-2 opacity-30" />No videos uploaded yet.{onPick ? ' Upload one from any exercise.' : ' Use the uploader above to add your first.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="video-library">
          {items.map(v => (
            <div key={v.id} className="regen-card-elevated overflow-hidden" data-testid={`video-${v.id}`}>
              <button type="button" onClick={() => onPick?.(v.url)} className={`block w-full ${onPick ? 'cursor-pointer' : 'cursor-default'}`}>
                <ExerciseMedia src={v.url} alt={v.original_name} className="w-full aspect-video" fit="contain" />
              </button>
              <div className="p-3">
                <div className="text-sm font-medium truncate" title={v.original_name}>{v.original_name || 'Video'}</div>
                <div className="text-[11px] text-muted-foreground font-mono">{fmtSize(v.size)}</div>
                <div className="flex gap-1.5 mt-2.5">
                  {onPick
                    ? <Button size="sm" onClick={() => onPick(v.url)} data-testid={`pick-video-${v.id}`} className="flex-1 bg-[#00C8FF] text-[#081726] font-semibold h-8"><Check className="w-3 h-3 mr-1" /> Use</Button>
                    : <Button size="sm" variant="outline" onClick={() => copy(v.url)} data-testid={`copy-video-${v.id}`} className="flex-1 border-[var(--c-hair)] h-8"><Copy className="w-3 h-3 mr-1" /> Copy URL</Button>}
                  <Button size="sm" variant="outline" onClick={() => del(v.id)} data-testid={`delete-video-${v.id}`} className="border-red-500/30 text-red-400 h-8 px-2"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
